/**
 * LocationNeededBanner (T12.6 D6, home-coords gate amended T12.6.1) — the
 * persistent "turn on location" banner.
 *
 * Shown when location is not WORKING — never granted, denied, or granted then
 * revoked at the OS level (`locationWorking === false`) — AND the household has
 * NO home coordinates. It consumes `useModeDetection`'s outcome signal
 * (`locationWorking`), which is keyed off the actual `getCurrentPosition` result,
 * NOT a `permissions.query` snapshot (which can lie after a Settings-level revoke,
 * D2). So a permission that was granted and later revoked still surfaces the
 * banner — but only for a household with no home-coords fallback.
 *
 * T12.6.1 — the home-coords gate. A household with a home postal code/address
 * already has full store discovery + deals coverage without GPS, so nagging it
 * app-wide (every screen, forever) is the wrong scope for the ask; GPS still adds
 * value there (the in-store auto-switch, the Deals in-store filter) but that
 * value is surfaced contextually instead (the Deals inline row, the ModeSpine
 * post-manual-switch hint — both dismissible with a 14-day re-surface,
 * `stores/dismissal.ts`). This ambient banner narrows to "no location at all":
 * `hasHomeCoords === true` hides it even while `locationWorking === false`.
 *
 * Persistent, not dismissible: it stays while location isn't working (and no
 * home-coords fallback exists) and disappears the moment a usable fix resolves
 * (`locationWorking === true`) or a home coordinate is set. A working fix that is
 * merely *elsewhere* (location works, just not near a store or home) does NOT
 * show it — that is a resolved outcome. While the outcome is still undetermined
 * (`null`) it renders nothing, so it never flashes before the entry attempt
 * resolves.
 *
 * Copy frames the VALUE (relevant store locations + deals), not the mechanism.
 * The primary action calls `requestDetection()` — on web/native `prompt` this
 * raises the OS prompt; where the OS won't re-prompt (a hard denial), the
 * secondary guidance directs the user to enable location in device Settings
 * (guidance text only — no settings-deeplink plugin, D6).
 *
 * Mounted ambiently in AppShell like ConfirmEmailNudge: on the real nav surfaces,
 * not the focused layover config screens. AppShell is the SOLE caller of
 * `useModeDetection` (its entry attempt is the one-shot detect); it passes the
 * outcome + home-coords presence + trigger here as props, so the banner never
 * spins up a second `getCurrentPosition` (which would double the OS prompt on
 * native). Token-only (CP5); mobile-first; ≥44px targets.
 */

import { type CSSProperties } from 'react';

export interface LocationNeededBannerProps {
  /**
   * `useModeDetection().locationWorking` — the OUTCOME signal. `null`
   * (undetermined) / `true` (working) render nothing; only `false` (not working)
   * can show the banner (still gated on `hasHomeCoords`).
   */
  locationWorking: boolean | null;
  /**
   * `useModeDetection().hasHomeCoords` (T12.6.1) — `true` hides the banner even
   * while `locationWorking === false`: a postal-located household already has
   * store discovery + deals covered, so the app-wide ask narrows to "no
   * location at all".
   */
  hasHomeCoords: boolean;
  /** `useModeDetection().requestDetection` — the enable action. */
  requestDetection: () => void;
}

// Exported for LocationNeededBanner.test.tsx — the test asserts on this source
// object rather than reading via CSSOM (happy-dom drops nested calc/var).
//
// BUG51 — this used to also carry `position: 'fixed'` + `left`/`right`/
// `bottom`/`zIndex`, identical to ConfirmEmailNudge's own `nudgeStyle`. When
// both were eligible at once (fresh signup: unverified + no location) they
// occupied the exact same rect — whichever painted second sat on top and
// absorbed the other's clicks. AppShell's shared `ambientBannerStackStyle` now
// owns the fixed positioning + z-index for both (mirroring ConfirmEmailNudge);
// this object is just the row's own visual + layout styling, so it renders as
// a plain flex child that stacks instead of floating independently.
export const bannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  borderRadius: 'var(--radius-lg)',
  backgroundColor: 'var(--color-accent-subtle)',
  boxShadow: 'var(--shadow-lg)',
};

const artStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'var(--color-bg-raised)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-base)',
};

const mainStyle: CSSProperties = { flex: 1, minWidth: 0 };

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  lineHeight: 1.3,
};

const descStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.35,
};

const ctaStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

export function LocationNeededBanner({
  locationWorking,
  hasHomeCoords,
  requestDetection,
}: LocationNeededBannerProps): JSX.Element | null {
  // Undetermined (before the entry attempt resolves) or working → render
  // nothing. Only `false` (not working) shows the banner. A working-but-elsewhere
  // fix is `true`, so it stays hidden.
  if (locationWorking !== false) return null;
  // T12.6.1 — a postal-located household already has store discovery + deals
  // covered without GPS; the app-wide ask narrows to "no location at all".
  if (hasHomeCoords) return null;

  return (
    <div
      style={bannerStyle}
      role="complementary"
      aria-label="Location needed"
      data-testid="location-needed-banner"
    >
      <div style={artStyle} aria-hidden="true">
        📍
      </div>
      <div style={mainStyle}>
        <div style={titleStyle}>Turn on location</div>
        <div style={descStyle}>
          Turn on location so Shopista can show stores and deals near you. If you
          turned it off before, enable it in your device Settings.
        </div>
      </div>
      <button
        type="button"
        style={ctaStyle}
        onClick={requestDetection}
        data-testid="location-needed-enable"
      >
        Turn on
      </button>
    </div>
  );
}
